import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PasswordField from "../components/PasswordField";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = location.state?.from || "/account";
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "", marketing_consent: true });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Your TAVEN account is ready");
      navigate(redirect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create your account");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-taven-gray/30" data-testid="register-page">
      <div className="w-full max-w-md bg-white border border-black/10 p-8 md:p-10">
        <div className="text-center mb-8">
          <span className="text-2xl font-black font-display tracking-tighter">TAVEN</span>
          <h1 className="text-xl font-black font-display mt-4">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">Join the TAVEN ecosystem — shop, learn, and grow.</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required value={form.first_name} onChange={set("first_name")} placeholder="First name" data-testid="reg-first" className="border border-black/15 focus:border-taven-gold px-4 py-3.5 text-sm outline-none" />
            <input value={form.last_name} onChange={set("last_name")} placeholder="Last name" data-testid="reg-last" className="border border-black/15 focus:border-taven-gold px-4 py-3.5 text-sm outline-none" />
          </div>
          <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="reg-email" className="w-full border border-black/15 focus:border-taven-gold px-4 py-3.5 text-sm outline-none" />
          <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="reg-phone" className="w-full border border-black/15 focus:border-taven-gold px-4 py-3.5 text-sm outline-none" />
          <PasswordField value={form.password} onChange={set("password")} placeholder="Password (min 6 characters)" testId="reg-password" icon={false} className="border" />
          <label className="flex items-start gap-2 text-xs text-muted-foreground py-1">
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} data-testid="reg-consent" className="mt-0.5" />
            Keep me updated with TAVEN offers, new arrivals and academy news.
          </label>
          <button disabled={loading} data-testid="reg-submit" className="w-full bg-taven-black text-white font-bold uppercase tracking-wider py-4 hover:bg-taven-orange transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="font-bold text-black hover:text-taven-gold" data-testid="to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
